import {
  BackSide,
  CanvasTexture,
  Color,
  LatheGeometry,
  Mesh,
  MeshBasicMaterial,
  MeshToonMaterial,
  NearestFilter,
  Vector2,
  type BufferGeometry,
  type Texture,
} from 'three'

/**
 * The look: banded toon shading plus an inked outline.
 *
 * Three redesigns of the fighters failed for the same reason, and it was not
 * the arrangement of the parts — it was that a smoothly-lit primitive reads as
 * *a primitive*. A sphere with a Lambert falloff is a sphere; the same sphere
 * with three flat bands of light and a dark contour around it is a drawing of a
 * head. That is the whole difference between "two capsules and legs" and a
 * character, and it costs one gradient texture and one duplicated mesh.
 *
 * Both halves matter and neither works alone:
 *  - **Banding** turns continuous shading into shape. It also survives bloom,
 *    which was smearing the soft gradients into a glow.
 *  - **The outline** (a back-facing hull, slightly inflated) gives every part a
 *    silhouette of its own, so limbs stay legible against the body at the
 *    twenty-two pixels this camera actually gives a fighter.
 */

let gradient: Texture | null = null

/**
 * A three-band ramp: shadow, mid, light. Nearest filtering is what makes the
 * steps hard — with linear filtering this is just a soft gradient again.
 */
export function toonGradient(): Texture {
  if (gradient) return gradient
  const canvas = document.createElement('canvas')
  canvas.width = 4
  canvas.height = 1
  const ctx = canvas.getContext('2d')
  if (ctx) {
    const stops = ['#3c4048', '#8b8f96', '#d2d6dc', '#ffffff']
    for (let i = 0; i < stops.length; i++) {
      ctx.fillStyle = stops[i]
      ctx.fillRect(i, 0, 1, 1)
    }
  }
  gradient = new CanvasTexture(canvas)
  gradient.minFilter = NearestFilter
  gradient.magFilter = NearestFilter
  gradient.generateMipmaps = false
  return gradient
}

export function toonMaterial(color: Color, emissiveStrength = 0.12): MeshToonMaterial {
  return new MeshToonMaterial({
    color,
    gradientMap: toonGradient(),
    emissive: color.clone().multiplyScalar(emissiveStrength),
  })
}

/**
 * Wraps a mesh in its own contour.
 *
 * A back-facing copy scaled up slightly: the front faces are culled, so all
 * that survives is a shell peeking out around the edges of the original. Cheap,
 * needs no post-process pass, and works on every shape here.
 */
export function inkOutline(mesh: Mesh, thickness = 0.055): Mesh {
  const outline = new Mesh(
    mesh.geometry,
    new MeshBasicMaterial({ color: 0x0b0d11, side: BackSide, toneMapped: false }),
  )
  outline.scale.setScalar(1 + thickness)
  // The outline must not cast a second shadow or the contact shadows double up.
  outline.castShadow = false
  outline.receiveShadow = false
  mesh.add(outline)
  return outline
}

/**
 * A body of revolution from a profile of (radius, height) pairs.
 *
 * This is the other half of "quality": a torso whose radius actually changes
 * along its length — a chest that tapers into a waist, a helmet with a jaw —
 * cannot be made by scaling a capsule, and scaling a capsule is exactly what
 * every previous version did. Profiles are authored in unit height so a plan
 * can stretch them without distorting the ends.
 */
export function lathe(profile: Array<[number, number]>, segments = 16): BufferGeometry {
  return new LatheGeometry(
    profile.map(([r, y]) => new Vector2(Math.max(r, 0.0001), y)),
    segments,
  )
}

/** Torso profiles, one per body archetype. Radius, then height, both unit. */
export const TORSO_PROFILES: Record<string, Array<[number, number]>> = {
  // Broad chest falling into a narrow waist: armour.
  capsule: [
    [0, 0],
    [0.62, 0.06],
    [0.86, 0.24],
    [1, 0.52],
    [0.92, 0.78],
    [0.66, 0.95],
    [0, 1],
  ],
  // Heavy, square-shouldered.
  box: [
    [0, 0],
    [0.78, 0.04],
    [0.94, 0.2],
    [1, 0.62],
    [1, 0.86],
    [0.7, 0.98],
    [0, 1],
  ],
  // A rounded carapace, widest low down.
  ovoid: [
    [0, 0],
    [0.7, 0.08],
    [1, 0.36],
    [0.98, 0.62],
    [0.72, 0.86],
    [0, 1],
  ],
}

/** Head profiles. The jaw and crown shapes are what tell them apart. */
export const HEAD_PROFILES: Record<string, Array<[number, number]>> = {
  sphere: [
    [0, 0],
    [0.55, 0.08],
    [0.92, 0.3],
    [1, 0.55],
    [0.86, 0.82],
    [0.44, 0.96],
    [0, 1],
  ],
  // Flat-topped helmet with a defined jaw.
  box: [
    [0, 0],
    [0.72, 0.03],
    [0.9, 0.14],
    [0.98, 0.42],
    [1, 0.76],
    [0.96, 0.94],
    [0, 1],
  ],
  // Long and tapered, for a beak to sit on.
  egg: [
    [0, 0],
    [0.4, 0.06],
    [0.74, 0.26],
    [0.86, 0.55],
    [0.74, 0.8],
    [0.38, 0.95],
    [0, 1],
  ],
}
